import { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import axios from 'axios';
import { API_URL } from '../utils/api';
import QRCodeDisplay from '../components/QRCodeDisplay';
import './CustomerDisplay.css';

const TEXTS = {
  ru: {
    hint: 'Отсканируйте QR-код телефоном\nчтобы следить за заказом',
    ctaTitle: 'Следи за заказом с телефона',
    ctaSub: 'Статус обновляется автоматически',
    recentLabel: 'Недавние заказы',
    order: 'Заказ',
    loading: 'Загрузка...',
    noOrders: 'Нет активных заказов',
    footer: 'ждём еду, а не у стойки',
  },
  en: {
    hint: 'Scan QR code with your phone\nto track your order',
    ctaTitle: 'Track your order on mobile',
    ctaSub: 'Status updates automatically',
    recentLabel: 'Recent orders',
    order: 'Order',
    loading: 'Loading...',
    noOrders: 'No active orders',
    footer: 'wait at your table, not the counter',
  },
  kk: {
    hint: 'Тапсырысыңызды қадағалау үшін\nQR кодты сканерлеңіз',
    ctaTitle: 'Телефоннан тапсырысты қадағала',
    ctaSub: 'Күй автоматты жаңарады',
    recentLabel: 'Соңғы тапсырыстар',
    order: 'Тапсырыс',
    loading: 'Жүктелуде...',
    noOrders: 'Белсенді тапсырыстар жоқ',
    footer: 'есепшіде емес, үстелде күтеміз',
  },
} as const;

type Lang = keyof typeof TEXTS;

interface Order {
  id: string;
  qrCode: string;
  qrCodeUrl?: string;
  status?: string;
  restaurant: { name: string };
  scannedAt?: string | null;
}

function CustomerDisplay() {
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [scannedOrderIds, setScannedOrderIds] = useState<Set<string>>(new Set());
  const socketRef = useRef<Socket | null>(null);
  const [restaurantId, setRestaurantId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState<Lang>('ru');

  const t = TEXTS[lang];

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rid = params.get('restaurantId');
    if (rid) {
      setRestaurantId(rid);
    } else {
      axios.get(`${API_URL}/api/restaurants`)
        .then(res => {
          if (res.data.length > 0) setRestaurantId(res.data[0].id);
          else setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, []);

  const fetchRecentOrders = async (rid: string) => {
    try {
      const base = API_URL || '';
      const response = await axios.get(`${base}/api/orders/restaurant/${rid}/public`, {
        params: { limit: 10 },
      });
      const orders = (Array.isArray(response.data) ? response.data : response.data.orders || []).filter(
        (o: Order) => o.qrCode && o.status !== 'COMPLETED' && o.status !== 'CANCELLED'
      );
      setRecentOrders(orders);
      setScannedOrderIds(prev => {
        const next = new Set(prev);
        orders.forEach((o: Order) => {
          if (o.scannedAt) next.add(o.id);
          else next.delete(o.id);
        });
        return next;
      });
      if (orders.length > 0) {
        const firstUnscanned = orders.find((o: Order) => !o.scannedAt);
        setCurrentOrder(prev => {
          if (!prev) return firstUnscanned ?? orders[0];
          const prevInList = orders.find((o: Order) => o.id === prev.id);
          if (prevInList && !prevInList.scannedAt) return prev;
          return firstUnscanned ?? orders[0];
        });
      }
    } catch {
      setRecentOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!restaurantId) return;

    let socketUrl = API_URL;
    if (!socketUrl) {
      const hostname = window.location.hostname;
      socketUrl = (hostname === 'localhost' || hostname === '127.0.0.1')
        ? 'http://localhost:3001'
        : `${window.location.protocol}//${hostname}:3001`;
    }

    const newSocket = io(socketUrl, { transports: ['websocket', 'polling'] });

    newSocket.on('connect', () => {
      console.log('[display] socket connected, joining restaurant:', restaurantId);
      newSocket.emit('join-restaurant', restaurantId);
    });

    newSocket.on('order-created', (order: Order) => {
      if ((order as any).restaurantId === restaurantId && order.qrCode) {
        setRecentOrders(prev => [order, ...prev.filter(o => o.id !== order.id)].slice(0, 10));
        setCurrentOrder(order);
      }
    });

    newSocket.on('qr-scanned', ({ orderId }: { orderId: string }) => {
      console.log('[display] qr-scanned received, orderId:', orderId);
      setScannedOrderIds(prev => new Set([...prev, orderId]));
    });

    newSocket.on('qr-show', ({ orderId }: { orderId: string }) => {
      setScannedOrderIds(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    });

    socketRef.current = newSocket;
    fetchRecentOrders(restaurantId);

    const interval = setInterval(() => fetchRecentOrders(restaurantId), 5000);

    // Reconnect socket + refetch when display wakes from idle
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (!newSocket.connected) {
        newSocket.connect();
      }
      newSocket.emit('join-restaurant', restaurantId);
      fetchRecentOrders(restaurantId);
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      socketRef.current = null;
      newSocket.close();
      clearInterval(interval);
    };
  }, [restaurantId]);

  const shortId = (id: string) => id?.slice(-6).toUpperCase();

  const orderUrl = currentOrder
    ? currentOrder.qrCodeUrl || `${window.location.origin}/order/${currentOrder.qrCode}?lang=${lang}`
    : '';

  const restaurantName = currentOrder?.restaurant?.name || 'OLSO';

  return (
    <div className="cd-container">

      {/* HEADER */}
      <div className="cd-header">
        <div className="cd-logo">
          <div className="cd-logo-dot" />
          OLSO
        </div>
        <div className="cd-restaurant-name">{restaurantName}</div>
        <div className="cd-lang-switcher">
          {(['ru', 'en', 'kk'] as Lang[]).map(l => (
            <button
              key={l}
              className={`cd-lang-btn${lang === l ? ' active' : ''}`}
              onClick={() => setLang(l)}
            >
              {l === 'ru' ? 'РУ' : l === 'en' ? 'EN' : 'ҚАЗ'}
            </button>
          ))}
        </div>
      </div>

      {/* MAIN */}
      <div className="cd-main">
        {loading ? (
          <div className="cd-loading">{t.loading}</div>
        ) : !currentOrder || scannedOrderIds.has(currentOrder.id) ? (
          <div className="cd-error">{t.noOrders}</div>
        ) : (
          <>
            <div className="cd-order-number">
              {t.order} #{shortId(currentOrder.id)}
            </div>

            <div className="cd-qr-card">
              <div className="cd-qr-hint">
                {t.hint.split('\n').map((line, i) => (
                  <span key={i}>{line}{i === 0 && <br />}</span>
                ))}
              </div>

              <div className="cd-qr-wrap">
                <QRCodeDisplay
                  url={orderUrl}
                  size={182}
                  alt="Order QR"
                  fallback={
                    <div style={{ width: 182, height: 182, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 12 }}>
                      QR...
                    </div>
                  }
                />
                <div className="cd-scan-line" />
              </div>

              <div className="cd-cta">
                <div className="cd-cta-icon"></div>
                <div>
                  <div className="cd-cta-title">{t.ctaTitle}</div>
                  <div className="cd-cta-sub">{t.ctaSub}</div>
                </div>
              </div>
            </div>

            {recentOrders.length > 1 && (
              <>
                <div className="cd-recent-label">{t.recentLabel}</div>
                <div className="cd-recent-list">
                  {recentOrders.slice(0, 6).map(order => (
                    <button
                      key={order.id}
                      className={`cd-recent-btn${order.id === currentOrder.id ? ' active' : ''}`}
                      onClick={() => setCurrentOrder(order)}
                    >
                      #{shortId(order.id)}
                    </button>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* FOOTER */}
      <div className="cd-footer">
        <div className="cd-footer-text">
          Powered by <span>OLSO</span> — {t.footer}
        </div>
      </div>

    </div>
  );
}

export default CustomerDisplay;
